/**
 * Central connectivity state. navigator.onLine alone is unreliable: a cold
 * start in an already-offline browser never fires an offline event, so the
 * flag keeps claiming "online" (VIVATISPDB-435). This module combines the
 * browser signal with the empirical one — did the last backend request
 * actually get through? — and recovers via a lightweight health probe.
 *
 * api.ts feeds this store from real request outcomes; consumers read it via
 * useOfflineStatus / isNetworkOnline.
 */

import { onlineManager } from '@tanstack/react-query';

const PROBE_URL = `${import.meta.env.VITE_BACKEND_URL ?? ''}/api/health`;
const PROBE_INTERVAL = 30 * 1000;

let backendReachable = true;
let probeTimer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function isNetworkOnline(): boolean {
  return navigator.onLine && backendReachable;
}

/** Called whenever a backend request received any HTTP response. */
export function reportBackendReachable(): void {
  stopProbing();
  if (!backendReachable) {
    backendReachable = true;
    // Resume paused queries — React Query treats "backend reachable again"
    // like coming back online.
    onlineManager.setOnline(true);
    emit();
  }
}

/** Called whenever a backend request failed at network level. */
export function reportBackendUnreachable(): void {
  if (backendReachable) {
    backendReachable = false;
    // Let React Query pause retries and refetches instead of hammering an
    // unreachable backend into error states (queries stay pending/paused
    // and keep their data; mutations use networkMode 'always' and are
    // handled by the offline queue).
    onlineManager.setOnline(false);
    emit();
  }
  startProbing();
}

export function subscribeConnectivity(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * While the backend is unreachable, periodically ping the health endpoint so
 * the app recovers even when the network returns without a browser online
 * event (which is exactly how the state got stuck in the first place).
 * Any HTTP response counts as reachable — only a network-level failure keeps
 * the probe running.
 */
async function probe(): Promise<void> {
  if (!navigator.onLine) return;
  try {
    await fetch(PROBE_URL, { cache: 'no-store' });
    reportBackendReachable();
  } catch {
    // Still unreachable — keep probing.
  }
}

function startProbing(): void {
  if (probeTimer !== null || typeof window === 'undefined') return;
  probeTimer = setInterval(() => {
    void probe();
  }, PROBE_INTERVAL);
}

function stopProbing(): void {
  if (probeTimer !== null) {
    clearInterval(probeTimer);
    probeTimer = null;
  }
}

const FLAG_POLL_INTERVAL = 5 * 1000;
let lastNavigatorOnline: boolean | null = null;

function handleWentOnline(): void {
  lastNavigatorOnline = navigator.onLine;
  // A fresh, trustworthy online signal — reset the empirical state
  // optimistically; the next real request corrects it if needed.
  stopProbing();
  backendReachable = true;
  onlineManager.setOnline(true);
  emit();
}

function handleWentOffline(): void {
  lastNavigatorOnline = navigator.onLine;
  onlineManager.setOnline(false);
  emit();
}

/**
 * Safety net for lost events: after an offline cold start (page served by
 * the service worker) browsers sometimes flip navigator.onLine without
 * delivering the online event to the document — without this poll the app
 * would stay "offline" until the next reload (VIVATISPDB-435).
 *
 * Exported for tests; runs on a 5s interval in the browser.
 */
export function pollNavigatorFlag(): void {
  if (typeof navigator === 'undefined' || navigator.onLine === lastNavigatorOnline) return;
  if (navigator.onLine) {
    handleWentOnline();
  } else {
    handleWentOffline();
  }
}

if (typeof window !== 'undefined') {
  lastNavigatorOnline = navigator.onLine;
  window.addEventListener('online', handleWentOnline);
  window.addEventListener('offline', handleWentOffline);
  setInterval(pollNavigatorFlag, FLAG_POLL_INTERVAL);
}
